import React, { useEffect, useRef, useState } from "react";
import "./StatBlock.scss";
import { useInViewOnce } from "../utils/useInViewOnce";

interface StatBlockProps {
  statPrefix?: string;
  stat: number;
  statLabel: string;
  label: React.ReactNode;
  variant?: "primary" | "secondary";
}

const StatBlock: React.FC<StatBlockProps> = ({
  statPrefix,
  stat,
  statLabel,
  label,
  variant = "primary",
}) => {
  const { ref, inView } = useInViewOnce<HTMLDivElement>({
    threshold: 0,
    rootMargin: "0px",
  });
  const [displayedNum, setDisplayedNum] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const durationMs = 600;

  useEffect(() => {
    if (!inView) return;

    const step = (ts: number) => {
      if (startTimeRef.current === null) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const progress = Math.min(elapsed / durationMs, 1);

      // Round during animation, but show exact value when complete
      if (progress < 1) {
        setDisplayedNum(Math.round(progress * stat));
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplayedNum(stat); // Show exact final value
      }
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [inView, stat]);

  return (
    <div ref={ref} className={`stat-block stat-block--${variant}`}>
      <span className="stat-number">
        <span className="number">
          {statPrefix}
          {displayedNum} {statLabel}
        </span>
      </span>
      <span className="label">{label}</span>
    </div>
  );
};

export default StatBlock;
